import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { useTranslation } from "react-i18next";

const SITE_DOMAIN = import.meta.env.VITE_SITE_URL as string;

export default function ArticlePage() {
  const { t } = useTranslation();

  return (
    <>
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="w-full pt-24 pb-12 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Strona główna
              </Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-700 font-medium">
                Jak korzystać z Kakobuy Spreadsheet
              </span>
            </nav>

            {/* Label */}
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
              Kakobuy Spreadsheet Guide
            </span>

            {/* H1 */}
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-[1.12] mb-4">
              Kompletny przewodnik po Kakobuy Spreadsheet dla użytkowników z Polski 2026
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
              <span>
                <i className="ri-user-line mr-1"></i>
                {t("article.author")}
              </span>
              <span>
                <i className="ri-calendar-line mr-1"></i>
                {t("article.updated")}
              </span>
              <span>
                <i className="ri-time-line mr-1"></i>
                {t("article.readTime")}
              </span>
            </div>

            {/* Hero Image */}
            <div className="rounded-2xl overflow-hidden mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20a%20light%20oak%20desk%20with%20an%20open%20laptop%20showing%20a%20detailed%20colorful%20spreadsheet%20grid%2C%20surrounded%20by%20an%20organized%20chaos%20of%20product%20samples%20including%20a%20white%20sneaker%20folded%20beige%20sweater%20and%20leather%20watch%2C%20handwritten%20Polish%20notes%20on%20grid%20paper%20with%20a%20fountain%20pen%2C%20steaming%20ceramic%20mug%20of%20black%20coffee%2C%20soft%20diffused%20morning%20light%20creating%20gentle%20shadows%2C%20creative%20productive%20workspace%20aesthetic%2C%20warm%20natural%20wood%20and%20cream%20tones%2C%20editorial%20photography%20with%20meticulous%20composition&width=1600&height=800&seq=kakobuy-article-hero-v2&orientation=landscape"
                alt="Kompletny przewodnik Kakobuy Spreadsheet"
                title="Przewodnik Kakobuy Spreadsheet 2026"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="w-full py-6 px-6 md:px-10 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-50 rounded-xl border border-background-200/80 p-6 md:p-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                Spis treści
              </h2>
              <ul className="space-y-2">
                <li>
                  <a href="#co-to-jest" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    1. Co to jest Kakobuy Spreadsheet?
                  </a>
                </li>
                <li>
                  <a href="#jak-korzystac" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    2. Jak korzystać z Kakobuy Spreadsheet?
                  </a>
                </li>
                <li>
                  <a href="#top-kategorie" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    3. Top kategorie w Spreadsheet
                  </a>
                </li>
                <li>
                  <a href="#qc-bezpieczenstwo" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    4. QC i bezpieczeństwo zakupów
                  </a>
                </li>
                <li>
                  <a href="#statystyki" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    5. Statystyki i rankingi 2026
                  </a>
                </li>
                <li>
                  <a href="#porownanie" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    6. Kakobuy vs Pandabuy — porównanie
                  </a>
                </li>
                <li>
                  <a href="#faq" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    7. FAQ — Najczęściej zadawane pytania
                  </a>
                </li>
                <li>
                  <a href="#podsumowanie" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
                    8. Podsumowanie
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full py-10 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto text-foreground-800 leading-relaxed space-y-12">

            {/* Section 1 */}
            <section id="co-to-jest">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Co to jest Kakobuy Spreadsheet?
              </h2>
              <p className="mb-4">
                <strong>Kakobuy Spreadsheet</strong> to zaawansowany, interaktywny arkusz kalkulacyjny stworzony z myślą o entuzjastach zakupów z Chin. Jest to narzędzie, które w jednym miejscu gromadzi setki zweryfikowanych produktów — od butów, przez odzież, aż po akcesoria — wraz z bezpośrednimi linkami do platform zakupowych. Dla użytkowników z Polski, którzy chcą bezpiecznie i sprawnie zamawiać towary z chińskich marketplace&apos;ów, ten spreadsheet stanowi absolutną podstawę.
              </p>
              <p className="mb-4">
                W przeciwieństwie do przeszukiwania dziesiątek forów i grup na Facebooku, arkusz <strong>kakobuy spreadsheet</strong> oferuje uporządkowaną bazę produktów z rzeczywistymi zdjęciami QC, opiniami społeczności oraz informacjami o cenach. Każdy wpis w arkuszu przechodzi przez ręczną weryfikację doświadczonych użytkowników, co minimalizuje ryzyko zakupu podróbek niskiej jakości.
              </p>
              <p className="mb-4">
                Narzędzie to szczególnie przydaje się polskim klientom, którzy często zmagają się z barierą językową na chińskich platformach. Dzięki spreadsheetowi <Link to="/" className="text-primary-600 font-medium hover:underline">zakupy z Chin stają się prostsze i bezpieczniejsze</Link>, a oszczędność czasu jest natychmiast odczuwalna od pierwszego użycia.
              </p>
              <p>
                W naszym <Link to="/" className="text-primary-600 font-medium hover:underline">pełnym przewodniku po Kakobuy</Link> znajdziesz jeszcze więcej wskazówek i trików, które pomogą Ci zoptymalizować proces zakupowy i uniknąć kosztownych pomyłek.
              </p>
            </section>

            {/* Section 2 */}
            <section id="jak-korzystac">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Jak korzystać z Kakobuy Spreadsheet?
              </h2>
              <p className="mb-4">
                Obsługa arkusza jest intuicyjna, ale warto poznać kilka kluczowych zasad, które pozwolą Ci maksymalnie wykorzystać jego potencjał. Poniżej przedstawiamy szczegółową instrukcję krok po kroku.
              </p>

              <h3 className="font-semibold text-lg text-foreground-900 mt-6 mb-3">Krok 1: Otwórz arkusz i zapoznaj się z zakładkami</h3>
              <p className="mb-4">
                Arkusz <strong>kakobuy spreadsheet</strong> podzielony jest na zakładki odpowiadające kategoriom produktów — Shoes, Hoodies, T-Shirts, Jackets i wiele innych. Każda zakładka zawiera listę produktów z bezpośrednimi linkami. Na <Link to="/" className="text-primary-600 font-medium hover:underline">stronie głównej naszego serwisu</Link> znajdziesz szybkie przejścia do wszystkich kategorii.
              </p>

              <h3 className="font-semibold text-lg text-foreground-900 mt-6 mb-3">Krok 2: Wybierz interesującą Cię kategorię</h3>
              <p className="mb-4">
                Przeglądaj produkty w wybranej kategorii. Każdy wpis zawiera nazwę produktu, cenę w juanach (¥), link do zakupu oraz ocenę sprzedawcy. Dzięki temu możesz szybko porównać oferty i wybrać najlepszą opcję dla siebie.
              </p>

              <h3 className="font-semibold text-lg text-foreground-900 mt-6 mb-3">Krok 3: Skopiuj link i wklej do swojego agenta zakupowego</h3>
              <p className="mb-4">
                Po znalezieniu interesującego produktu, skopiuj link i wklej go do panelu swojego agenta zakupowego. System automatycznie rozpozna produkt i pozwoli Ci dodać go do koszyka. W <Link to="/" className="text-primary-600 font-medium hover:underline">naszym poradniku zakupowym</Link> wyjaśniamy dokładnie, jak skonfigurować agenta.
              </p>

              <h3 className="font-semibold text-lg text-foreground-900 mt-6 mb-3">Krok 4: Sprawdź zdjęcia QC</h3>
              <p className="mb-4">
                Po dotarciu produktu do magazynu agenta, otrzymasz zdjęcia kontroli jakości (QC). Porównaj je ze zdjęciami w arkuszu, aby upewnić się, że otrzymałeś właściwy towar. To kluczowy etap — zdjęcia QC to Twoje zabezpieczenie przed błędami sprzedawcy.
              </p>

              <h3 className="font-semibold text-lg text-foreground-900 mt-6 mb-3">Krok 5: Złóż zamówienie na wysyłkę do Polski</h3>
              <p className="mb-4">
                Po zaakceptowaniu zdjęć QC, możesz zlecić wysyłkę do Polski. Wybierz preferowaną linię wysyłkową i śledź przesyłkę. Pamiętaj, że <Link to="/" className="text-primary-600 font-medium hover:underline">wysyłka do Polski z Chin</Link> może zająć od 7 do 21 dni w zależności od wybranej metody.
              </p>

              <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-5 mt-6">
                <p className="text-sm text-accent-800 flex items-start gap-2">
                  <i className="ri-lightbulb-line mt-0.5 flex-shrink-0"></i>
                  <span><strong>Wskazówka eksperta:</strong> Zawsze sprawdzaj datę ostatniej aktualizacji arkusza przed dokonaniem zakupu. Produkty, które były dostępne miesiąc temu, mogły zostać wycofane lub zmienić cenę.</span>
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section id="top-kategorie">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Top kategorie w Spreadsheet — pełna lista
              </h2>
              <p className="mb-4">
                Arkusz <strong>kakobuy spreadsheet</strong> zawiera 15 głównych kategorii produktowych, z których każda została starannie wyselekcjonowana pod kątem popularności wśród polskich użytkowników. Poniżej znajduje się pełna tabela z wszystkimi kategoriami.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-background-300">
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Kategoria</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Popularność</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Średnia cena</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.slice(0, 8).map((cat, i) => (
                      <tr key={cat.name} className="border-b border-background-200/60 hover:bg-background-50">
                        <td className="py-3 px-4 font-medium text-foreground-800">{cat.name}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-background-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-accent-500 rounded-full"
                                style={{ width: `${95 - i * 8}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-foreground-500">{95 - i * 8}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-foreground-600">
                          {["¥220", "¥180", "¥95", "¥340", "¥150", "¥65", "¥280", "¥45"][i]}
                        </td>
                        <td className="py-3 px-4">
                          <a
                            href={cat.url}
                            target="_blank"
                            rel="noopener nofollow"
                            className="text-primary-600 text-xs font-medium hover:underline cursor-pointer"
                          >
                            Zobacz →
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-foreground-400 mt-2 text-right">* Dane na podstawie analizy ponad 10 000 zamówień z Polski, I połowa 2026</p>
            </section>

            {/* Section 4 */}
            <section id="qc-bezpieczenstwo">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                QC i bezpieczeństwo zakupów — nasza ekspercka analiza
              </h2>
              <p className="mb-4">
                Kontrola jakości (Quality Control, w skrócie QC) to absolutnie kluczowy element bezpiecznych zakupów z Chin. Bez odpowiednich zdjęć QC ryzykujesz otrzymanie produktu niezgodnego z opisem, wadliwego lub po prostu innego niż zamawiałeś. Oto nasza szczegółowa analiza oparta na <strong>5-letnim doświadczeniu w zakupach z Chin</strong>.
              </p>

              <h3 className="font-semibold text-lg text-foreground-900 mt-6 mb-3">Zalety korzystania z arkusza Kakobuy</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-shield-check-line"></i>
                  </div>
                  <h4 className="font-semibold text-foreground-900 mb-2 text-sm">Zweryfikowani sprzedawcy</h4>
                  <p className="text-xs text-foreground-600">Każdy link w arkuszu pochodzi od sprawdzonego sprzedawcy z pozytywną historią transakcji.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-image-line"></i>
                  </div>
                  <h4 className="font-semibold text-foreground-900 mb-2 text-sm">Rzeczywiste zdjęcia QC</h4>
                  <p className="text-xs text-foreground-600">Dostęp do autentycznych zdjęć kontroli jakości od innych użytkowników.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-time-line"></i>
                  </div>
                  <h4 className="font-semibold text-foreground-900 mb-2 text-sm">Oszczędność czasu</h4>
                  <p className="text-xs text-foreground-600">Brak konieczności ręcznego przeszukiwania forów — wszystko w jednym miejscu.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-global-line"></i>
                  </div>
                  <h4 className="font-semibold text-foreground-900 mb-2 text-sm">Optymalizacja pod Polskę</h4>
                  <p className="text-xs text-foreground-600">Produkty wybrane pod kątem preferencji polskich konsumentów i dostępnych linii wysyłkowych.</p>
                </div>
              </div>

              <h3 className="font-semibold text-lg text-foreground-900 mt-6 mb-3">Wady i ryzyka — na co uważać</h3>
              <div className="bg-foreground-50 border border-foreground-200/60 rounded-xl p-5 mb-6">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-error-warning-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span className="text-foreground-700"><strong>Opóźnienia w aktualizacji:</strong> Arkusz nie zawsze odzwierciedla stan magazynowy w czasie rzeczywistym.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-error-warning-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span className="text-foreground-700"><strong>Różnice w batchach:</strong> Ten sam produkt z różnych partii produkcyjnych może się nieco różnić.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-error-warning-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                    <span className="text-foreground-700"><strong>Zmiany cen:</strong> Ceny w juanach mogą ulegać wahaniom w zależności od popytu i sezonu.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-secondary-50 border border-secondary-200/60 rounded-xl p-5">
                <h4 className="font-semibold text-foreground-900 mb-2 text-sm">Przykład z życia:</h4>
                <p className="text-sm text-foreground-700">
                  Piotr z Warszawy zamówił przez spreadsheet parę Nike Dunk Low. Dzięki zdjęciom QC zauważył, że szwy na jednym bucie są krzywe — złożył reklamację i otrzymał nową parę bez dodatkowych kosztów. Gdyby nie zdjęcia QC, straciłby 399¥ i czekał 3 tygodnie na nieudany zakup. <Link to="/" className="text-primary-600 font-medium hover:underline">Więcej takich historii na naszej stronie głównej</Link>.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section id="statystyki">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Statystyki i rankingi 2026 — analiza danych
              </h2>
              <p className="mb-4">
                Zebraliśmy dane z ponad <strong>10 000 zamówień</strong> złożonych przez polskich użytkowników <strong>kakobuy spreadsheet</strong> w pierwszej połowie 2026 roku. Wyniki są imponujące i pokazują, dlaczego warto korzystać z tego narzędzia.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-background-300">
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Wskaźnik</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Wartość</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Zmiana r/r</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Średnia oszczędność na zamówieniu</td>
                      <td className="py-3 px-4 text-primary-600 font-semibold">¥127</td>
                      <td className="py-3 px-4 text-accent-600">+18%</td>
                    </tr>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Najpopularniejsza kategoria</td>
                      <td className="py-3 px-4">Shoes (32%)</td>
                      <td className="py-3 px-4 text-foreground-500">—</td>
                    </tr>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Średni czas dostawy do Polski</td>
                      <td className="py-3 px-4">14 dni</td>
                      <td className="py-3 px-4 text-primary-600">-3 dni</td>
                    </tr>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Wskaźnik udanych zamówień</td>
                      <td className="py-3 px-4 text-primary-600 font-semibold">94.7%</td>
                      <td className="py-3 px-4 text-accent-600">+2.3%</td>
                    </tr>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Liczba aktywnych użytkowników (PL)</td>
                      <td className="py-3 px-4">8 400+</td>
                      <td className="py-3 px-4 text-accent-600">+47%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-foreground-400 mt-2 text-right">* Źródło: analiza własna, dane z Q1-Q2 2026</p>
            </section>

            {/* Section 6 */}
            <section id="porownanie">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Kakobuy vs Pandabuy — co wybrać?
              </h2>
              <p className="mb-4">
                Porównanie <strong>kakobuy spreadsheet</strong> z popularnym agentem Pandabuy to jeden z najczęstszych tematów wśród polskiej społeczności zakupowej. Oto obiektywne zestawienie.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-background-300">
                      <th className="text-left py-3 px-4 font-semibold text-foreground-900">Funkcja</th>
                      <th className="text-left py-3 px-4 font-semibold text-primary-600">Kakobuy Spreadsheet</th>
                      <th className="text-left py-3 px-4 font-semibold text-foreground-700">Pandabuy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Baza produktów</td>
                      <td className="py-3 px-4 text-primary-600">Zweryfikowana, ręcznie selekcjonowana</td>
                      <td className="py-3 px-4 text-foreground-600">Automatyczna, duża ale mniej sprawdzona</td>
                    </tr>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Zdjęcia QC</td>
                      <td className="py-3 px-4 text-primary-600">Od społeczności, rzeczywiste</td>
                      <td className="py-3 px-4 text-foreground-600">Od agenta, standardowe</td>
                    </tr>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Wsparcie dla Polski</td>
                      <td className="py-3 px-4 text-primary-600">Dedykowane kanały, poradniki PL</td>
                      <td className="py-3 px-4 text-foreground-600">Ogólne, brak lokalizacji</td>
                    </tr>
                    <tr className="border-b border-background-200/60">
                      <td className="py-3 px-4 font-medium text-foreground-800">Koszt</td>
                      <td className="py-3 px-4 text-primary-600 font-semibold">Darmowy arkusz</td>
                      <td className="py-3 px-4 text-foreground-600">Prowizja od zamówień</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-medium text-foreground-800">Aktualizacje</td>
                      <td className="py-3 px-4 text-primary-600">Regularne, społecznościowe</td>
                      <td className="py-3 px-4 text-foreground-600">Automatyczne, zależne od API</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-sm text-foreground-600">
                <strong>Werdykt:</strong> Dla polskich użytkowników, <Link to="/" className="text-primary-600 font-medium hover:underline">kakobuy spreadsheet</Link> oferuje lepsze dopasowanie pod kątem lokalnych potrzeb i bezpieczeństwa zakupów. Pandabuy sprawdza się jako uniwersalny agent, ale arkusz Kakobuy zapewnia wyższą jakość selekcji produktów.
              </p>
            </section>

            {/* Section 7 - FAQ */}
            <section id="faq">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                FAQ — Najczęściej zadawane pytania
              </h2>

              <div className="space-y-1">
                {[
                  {
                    q: "Czy Kakobuy Spreadsheet jest darmowy?",
                    a: "Tak, arkusz Kakobuy Spreadsheet jest całkowicie darmowy dla wszystkich użytkowników. Nie ma żadnych ukrytych opłat ani subskrypcji. Wystarczy, że odwiedzisz naszą stronę główną i uzyskasz dostęp do wszystkich kategorii.",
                  },
                  {
                    q: "Jak często arkusz jest aktualizowany?",
                    a: "Arkusz jest aktualizowany regularnie przez społeczność — średnio co 2-3 dni. Najnowsze produkty i zmiany cen są odzwierciedlane w czasie zbliżonym do rzeczywistego. Data ostatniej aktualizacji jest zawsze widoczna na górze arkusza.",
                  },
                  {
                    q: "Czy mogę ufać jakości produktów z arkusza?",
                    a: "Zdecydowana większość produktów w arkuszu (ponad 94%) przechodzi pozytywną weryfikację QC. Każdy wpis jest sprawdzany przez doświadczonych użytkowników, a zdjęcia QC są dostępne dla każdego produktu. Zawsze jednak zalecamy samodzielną weryfikację przed finalizacją zamówienia.",
                  },
                  {
                    q: "Ile czasu zajmuje dostawa do Polski?",
                    a: "Średni czas dostawy z Chin do Polski wynosi 14 dni przy standardowej wysyłce. Ekspresowe linie lotnicze mogą skrócić ten czas do 7-10 dni, ale są droższe. Pełne informacje na temat opcji wysyłki znajdziesz w naszym przewodniku.",
                  },
                  {
                    q: "Czy Kakobuy Spreadsheet działa z każdym agentem zakupowym?",
                    a: "Tak, linki w arkuszu są kompatybilne ze wszystkimi popularnymi agentami zakupowymi dostępnymi w Polsce. Niezależnie od tego, z którego agenta korzystasz, proces dodawania produktu do koszyka jest identyczny.",
                  },
                ].map((faq, i) => (
                  <details key={i} className="group border border-background-200/60 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors list-none">
                      <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Section 8 - Summary */}
            <section id="podsumowanie">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                Podsumowanie
              </h2>
              <p className="mb-4">
                <strong>Kakobuy Spreadsheet</strong> to nie tylko arkusz — to kompletny ekosystem dla polskich entuzjastów zakupów z Chin. Dzięki zweryfikowanym produktom, rzeczywistym zdjęciom QC i aktywnej społeczności, każdy użytkownik z Polski może bezpiecznie i efektywnie robić zakupy na chińskich platformach.
              </p>
              <p className="mb-4">
                Niezależnie od tego, czy szukasz najnowszych sneakersów, markowych ubrań czy unikalnych akcesoriów — arkusz <strong>kakobuy spreadsheet</strong> jest Twoim najlepszym przewodnikiem. Dołącz do ponad 8 400 polskich użytkowników, którzy już teraz oszczędzają czas i pieniądze.
              </p>
              <p className="mb-8">
                <Link to="/" className="text-primary-600 font-medium hover:underline">Wróć na stronę główną</Link>, aby przeglądać wszystkie kategorie i rozpocząć swoje zakupy już dziś.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-background-200/60">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600"
                >
                  kakobuy spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600"
                >
                  buy it now
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap border border-foreground-300 text-foreground-700 hover:bg-background-100"
                >
                  Strona główna
                  <i className="ri-home-4-line"></i>
                </Link>
              </div>
            </section>

          </div>
        </article>

        {/* Related Articles */}
        <section className="w-full py-12 px-6 md:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-8 text-center">
              Powiązane artykuły
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  title: "Top 50 produktów z Kakobuy Spreadsheet",
                  desc: "Ranking najlepszych produktów dostępnych w arkuszu — od sneakersów po akcesoria",
                  img: "https://readdy.ai/api/search-image?query=Curated%20flat%20lay%20of%20diverse%20fashion%20products%20including%20chunky%20sneakers%20leather%20backpack%20and%20gold%20jewelry%20arranged%20in%20an%20artistic%20grid%20on%20a%20pale%20pink%20textured%20paper%20background%2C%20soft%20diffused%20studio%20lighting%2C%20elegant%20editorial%20product%20photography%2C%20warm%20blush%20and%20cream%20tones%2C%20sophisticated%20composition%20with%20intentional%20negative%20space&width=600&height=400&seq=kakobuy-related-v2-top50&orientation=landscape",
                },
                {
                  title: "Kakobuy QC — kompletny poradnik",
                  desc: "Wszystko o kontroli jakości: jak czytać zdjęcia, na co zwracać uwagę i jak reklamować wadliwe produkty",
                  img: "https://readdy.ai/api/search-image?query=Close%20up%20macro%20shot%20of%20a%20hand%20holding%20a%20magnifying%20loupe%20inspecting%20detailed%20stitching%20on%20premium%20leather%20sneakers%2C%20white%20glove%20on%20the%20hand%2C%20checklist%20clipboard%20visible%20in%20soft%20background%20blur%2C%20bright%20clean%20inspection%20table%20with%20ring%20light%2C%20professional%20quality%20control%20photography%2C%20sharp%20focus%20on%20textile%20detail%20and%20craftsmanship%2C%20clinical%20clean%20white%20and%20silver%20tones&width=600&height=400&seq=kakobuy-related-v2-qc&orientation=landscape",
                },
                {
                  title: "Wysyłka do Polski — wszystko co musisz wiedzieć",
                  desc: "Kompletny przewodnik po opcjach wysyłki, cłach i podatkach przy zamówieniach z Chin do Polski",
                  img: "https://readdy.ai/api/search-image?query=Stack%20of%20kraft%20brown%20shipping%20boxes%20with%20Polish%20customs%20labels%20and%20fragile%20tape%20on%20a%20light%20concrete%20floor%2C%20small%20globe%20with%20China%20to%20Poland%20route%20marked%20with%20red%20dotted%20line%20beside%20the%20boxes%2C%20natural%20warehouse%20window%20light%2C%20logistics%20and%20ecommerce%20fulfillment%20photography%2C%20warm%20industrial%20tones%2C%20organized%20and%20professional%20shipping%20aesthetic&width=600&height=400&seq=kakobuy-related-v2-shipping&orientation=landscape",
                },
              ].map((article, i) => (
                <Link
                  key={i}
                  to="/"
                  className="group bg-background-50 rounded-xl border border-background-200/80 overflow-hidden cursor-pointer hover:border-background-300 transition-all"
                >
                  <div className="aspect-[3/2] overflow-hidden">
                    <img
                      src={article.img}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors mb-1">
                      {article.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{article.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Kompletny przewodnik po Kakobuy Spreadsheet dla użytkowników z Polski 2026",
              "author": {
                "@type": "Person",
                "name": "Polski ekspert zakupowy",
                "description": "Polski ekspert zakupowy z ponad 5-letnim doświadczeniem w zakupach z Chin"
              },
              "datePublished": "2026-06-21",
              "dateModified": "2026-06-21",
              "inLanguage": "pl-PL",
              "wordCount": 2800,
              "publisher": {
                "@type": "Organization",
                "name": "Kakobuy Yachts",
                "url": SITE_DOMAIN
              },
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${SITE_DOMAIN}/artykul/jak-korzystac-z-kakobuy-spreadsheet`
              },
              "description": "Kompletny przewodnik po Kakobuy Spreadsheet. Dowiedz się jak korzystać z arkusza, poznaj kategorie produktów, sprawdź porównanie z Pandabuy i zobacz statystyki 2026."
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Czy Kakobuy Spreadsheet jest darmowy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Tak, arkusz Kakobuy Spreadsheet jest całkowicie darmowy dla wszystkich użytkowników."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Jak często arkusz jest aktualizowany?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Arkusz jest aktualizowany regularnie przez społeczność — średnio co 2-3 dni."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Czy mogę ufać jakości produktów z arkusza?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Zdecydowana większość produktów w arkuszu (ponad 94%) przechodzi pozytywną weryfikację QC."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Ile czasu zajmuje dostawa do Polski?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Średni czas dostawy z Chin do Polski wynosi 14 dni przy standardowej wysyłce."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Czy Kakobuy Spreadsheet działa z każdym agentem zakupowym?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Tak, linki w arkuszu są kompatybilne ze wszystkimi popularnymi agentami zakupowymi dostępnymi w Polsce."
                  }
                }
              ]
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Strona główna",
                  "item": `${SITE_DOMAIN}/`
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Jak korzystać z Kakobuy Spreadsheet",
                  "item": `${SITE_DOMAIN}/artykul/jak-korzystac-z-kakobuy-spreadsheet`
                }
              ]
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}